import { ApiError } from '@/lib/api';
import { translateBillingError } from '@/lib/billingErrors';
import type { InvoicePayerPartyType } from '@/types';
import type {
  CreateStandaloneInvoiceInput,
  InvoicePayerInput,
} from '@/hooks/useInvoices';
import { draftLinesForSave } from './draftInvoiceForm';
import { parseEditorDecimal, summarizeEditorLines, type InvoiceEditorLine } from './invoiceEditorLines';

type TFunction = (key: string, options?: Record<string, unknown>) => string;

export type StandaloneInvoiceValidationKey =
  | 'createStandalone.payerRequired'
  | 'edit.linesRequired'
  | 'edit.lineDescriptionRequired'
  | 'edit.positiveQuantityRequired'
  | 'error.nonPositive';

export function buildStandaloneInvoicePayer(
  partyType: InvoicePayerPartyType,
  selectedId: string | null,
): InvoicePayerInput | null {
  if (!selectedId) return null;
  if (partyType === 'person') {
    return { partyType: 'person', personId: selectedId };
  }
  return { partyType, companyId: selectedId };
}

export function standaloneInvoiceValidationKey(
  payer: InvoicePayerInput | null,
  lines: readonly InvoiceEditorLine[],
): StandaloneInvoiceValidationKey | null {
  if (!payer) return 'createStandalone.payerRequired';
  if (payer.partyType === 'person' && !payer.personId) return 'createStandalone.payerRequired';
  if ((payer.partyType === 'company' || payer.partyType === 'insurer') && !payer.companyId) {
    return 'createStandalone.payerRequired';
  }
  if (lines.length === 0) return 'edit.linesRequired';
  if (lines.some((line) => !line.description.trim())) return 'edit.lineDescriptionRequired';
  if (lines.some((line) => parseEditorDecimal(line.quantity) <= 0)) {
    return 'edit.positiveQuantityRequired';
  }
  if (summarizeEditorLines(lines).totalCents <= 0) return 'error.nonPositive';
  return null;
}

export function buildStandaloneInvoiceBody(
  payer: InvoicePayerInput,
  lines: readonly InvoiceEditorLine[],
): CreateStandaloneInvoiceInput {
  return {
    payer,
    lines: draftLinesForSave(lines),
  };
}

export function translateCreateStandaloneError(err: unknown, t: TFunction): string {
  if (err instanceof ApiError && err.data && typeof err.data === 'object') {
    const code = (err.data as { code?: unknown }).code;
    if (code === 'payer_not_found') return t('error.payerNotFound');
  }
  return translateBillingError(err, t);
}
